import type { Participant } from '@crowdset/contracts';
import { assignSlots } from '@crowdset/domain';
import { Avatar, Backdrop, DjBooth, type AvatarState } from '@crowdset/ui';
import { memo, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useRoomStore } from '../../room/store';
import { cx } from '../../ui/kit';
import { UserMenu } from './UserMenu';

/** Slot geometry: back rows first. x/y are % of the crowd area (y = foot line). */
const ROWS = [
  { count: 9, y: 36, scale: 0.68 },
  { count: 10, y: 52, scale: 0.76 },
  { count: 10, y: 68, scale: 0.84 },
  { count: 11, y: 84, scale: 0.92 },
  { count: 10, y: 99, scale: 1 },
];
const SLOTS = ROWS.flatMap((r, ri) =>
  Array.from({ length: r.count }, (_, i) => {
    const span = 88;
    const step = span / r.count;
    const x = 6 + step * (i + 0.5) + (ri % 2 ? step * 0.25 : -step * 0.25);
    return { x, y: r.y, scale: r.scale, z: ri };
  }),
);
// Fill order: front-centre first so small crowds look natural.
const FILL_ORDER = SLOTS.map((s, i) => ({ i, d: Math.abs(s.x - 50) / 50 + (100 - s.y) / 40 }))
  .sort((a, b) => a.d - b.d)
  .map((s) => s.i);

export const SLOT_COUNT = SLOTS.length;

export function Stage({ player, compact = false }: { player: ReactNode; compact?: boolean }) {
  const data = useRoomStore((s) => s.data)!;
  const { room, play, queue } = data;
  const dj = play ? data.participants.find((p) => p.userId === play.dj.id) : null;

  return (
    <section
      aria-label="Room stage"
      className={cx(
        'relative isolate flex min-h-0 flex-col overflow-hidden',
        compact ? 'rounded-none' : 'rounded-[1.5rem] border border-white/[0.06]',
      )}
    >
      <Backdrop theme={room.theme} className="absolute inset-0 -z-10 h-full w-full" />
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-ink-950/35 via-transparent to-ink-950/60" />

      {!compact && <div className="flex justify-center px-4 pt-4 xl:px-6 xl:pt-5">{player}</div>}

      {/* DJ booth row */}
      <div
        className={cx(
          'relative flex shrink-0 items-end justify-center',
          compact ? 'h-[120px]' : 'h-[160px]',
        )}
      >
        <div className={cx('relative', compact ? 'w-[210px]' : 'w-[290px]')}>
          {/* DJ stands behind the booth: feet hidden, torso and head above the deck. */}
          <div className="absolute inset-x-0 bottom-[55%] flex justify-center">
            {play ? (
              <Avatar
                avatarId={play.dj.avatarId}
                state="dj"
                size={compact ? 52 : 70}
                highlight={play.dj.id === data.me.userId}
                title={`${play.dj.displayName} is DJing`}
              />
            ) : null}
          </div>
          <DjBooth theme={room.theme} className="relative block w-full" />
          <div className="absolute inset-x-0 bottom-[16%] flex justify-center">
            <span className="max-w-[80%] truncate rounded-full bg-ink-950/80 px-2.5 py-0.5 text-[11px] font-medium text-fg">
              {play ? (dj?.displayName ?? play.dj.displayName) : 'Booth open'}
            </span>
          </div>
        </div>
        {queue.length > 0 && !compact && (
          <div
            className="absolute bottom-3 right-4 hidden items-end gap-1 rounded-2xl bg-ink-950/60 px-3 py-2 backdrop-blur-sm md:flex"
            aria-label="Next DJs"
          >
            <span className="mr-1 self-center font-mono text-[10px] uppercase tracking-wider text-fg-subtle">
              Next
            </span>
            {queue.slice(0, 3).map((q) => (
              <div
                key={q.user.id}
                className="flex flex-col items-center"
                title={q.user.displayName}
              >
                <Avatar avatarId={q.user.avatarId} size={26} state="idle" />
              </div>
            ))}
            {queue.length > 3 && (
              <span className="self-center text-xs text-fg-muted">+{queue.length - 3}</span>
            )}
          </div>
        )}
      </div>

      <Crowd compact={compact} />
    </section>
  );
}

const Crowd = memo(function Crowd({ compact }: { compact: boolean }) {
  const data = useRoomStore((s) => s.data)!;
  const djId = data.play?.dj.id ?? null;
  const crowd = useMemo(
    () =>
      data.participants
        .filter((p) => !(djId && p.userId === djId))
        .sort((a, b) => a.joinedAtMs - b.joinedAtMs),
    [data.participants, djId],
  );
  const prev = useRef(new Map<string, number>());
  const slots = useMemo(() => {
    const next = assignSlots(
      prev.current,
      crowd.map((p) => p.participantId),
      SLOT_COUNT,
    );
    prev.current = next;
    return next;
  }, [crowd]);
  const overflow = crowd.length - slots.size;
  const base = compact ? 46 : 70;

  return (
    <div
      className={cx(
        'relative mx-auto w-full max-w-[1100px] flex-1',
        compact ? 'min-h-[150px]' : 'min-h-[190px]',
      )}
    >
      {crowd.map((p) => {
        const slot = slots.get(p.participantId);
        if (slot === undefined) return null;
        const s = SLOTS[FILL_ORDER[slot]!]!;
        return (
          <CrowdMember
            key={p.participantId}
            p={p}
            x={s.x}
            y={s.y}
            z={s.z}
            size={Math.round(base * s.scale)}
            isMe={p.participantId === data.me.participantId}
            vote={p.userId ? data.reactions.votes[p.userId] : undefined}
            burst={p.userId ? data.bursts[p.userId] : undefined}
            playing={!!data.play}
          />
        );
      })}
      {overflow > 0 && (
        <span className="absolute bottom-2 right-3 rounded-full bg-ink-950/80 px-2 py-0.5 font-mono text-[11px] text-fg-muted">
          +{overflow} more
        </span>
      )}
      {crowd.length === 0 && (
        <p className="absolute inset-x-0 bottom-6 text-center text-sm text-fg-muted">
          The floor is empty.
        </p>
      )}
    </div>
  );
});

const CrowdMember = memo(function CrowdMember({
  p,
  x,
  y,
  z,
  size,
  isMe,
  vote,
  burst,
  playing,
}: {
  p: Participant;
  x: number;
  y: number;
  z: number;
  size: number;
  isMe: boolean;
  vote: 'woot' | 'meh' | undefined;
  burst: number | undefined;
  playing: boolean;
}) {
  // A Woot plays a short celebration, then settles into dancing.
  const [celebrating, setCelebrating] = useState(false);
  useEffect(() => {
    if (!burst) return;
    setCelebrating(true);
    const t = setTimeout(() => setCelebrating(false), 2200);
    return () => clearTimeout(t);
  }, [burst]);
  const state: AvatarState = celebrating
    ? 'woot'
    : vote === 'woot'
      ? 'dance'
      : vote === 'meh'
        ? 'meh'
        : 'idle';
  const phase = (p.participantId.charCodeAt(p.participantId.length - 1) % 10) / 3;

  const body = (
    <button
      tabIndex={-1}
      className={cx(
        'group absolute flex -translate-x-1/2 -translate-y-full flex-col items-center outline-none transition-opacity duration-500',
        !p.connected && 'opacity-45',
      )}
      style={{ left: `${x}%`, top: `${y}%`, zIndex: z * 2 + (isMe ? 1 : 0) }}
      aria-hidden="true"
    >
      <Avatar
        avatarId={p.avatarId}
        state={playing ? state : 'idle'}
        size={size}
        phase={phase}
        highlight={isMe}
        burstKey={celebrating ? burst : null}
      />
      <span
        className={cx(
          'pointer-events-none absolute -bottom-4 whitespace-nowrap rounded-full px-1.5 py-px text-[10px] font-medium',
          isMe
            ? 'bg-accent text-accent-ink'
            : 'bg-ink-950/85 text-fg opacity-0 transition-opacity group-hover:opacity-100',
        )}
      >
        {isMe ? 'You' : p.displayName}
      </span>
    </button>
  );
  return <UserMenu participant={p}>{body}</UserMenu>;
});
